import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  DialogTitle,
  Divider,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Modal,
  ModalDialog,
  Stack,
  Switch,
  Typography,
} from "@mui/joy";
import BugReportIcon from "@mui/icons-material/BugReport";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Page from "../components/Pages/Page";
import PageHeader from "../components/PageHeader";
import Loading from "../components/Loading";
import { useOrgSettingsContext } from "../providers/OrgSettingsProvider";
import {
  useAddDomain,
  useDeleteDomain,
  useGetVerificationInstructions,
  useListDomains,
  useUpdateAutoEnrollment,
  useVerifyDomain,
} from "../services/api";
import { Domain } from "../common/types";
import { isDevFeaturesEnabled } from "../utils/devFeatures";

const normalizeDomain = (domain: string): string => {
  let normalized = domain.trim().toLowerCase();
  normalized = normalized.replace(/^https?:\/\//, "");
  normalized = normalized.replace(/^www\./, "");
  normalized = normalized.replace(/\/$/, "");
  if (!normalized || normalized.includes("/") || normalized.includes(" ")) {
    throw new Error("Invalid domain format");
  }
  return normalized;
};

const OrgSettingsDomains: React.FC = () => {
  const { user, organizationDetails } = useOrgSettingsContext();
  const addDomainAPI = useAddDomain();
  const listDomainsAPI = useListDomains();
  const getVerificationInstructionsAPI = useGetVerificationInstructions();
  const verifyDomainAPI = useVerifyDomain();
  const deleteDomainAPI = useDeleteDomain();
  const updateAutoEnrollmentAPI = useUpdateAutoEnrollment();

  const [domains, setDomains] = useState<Domain[]>([]);
  const [loadingDomains, setLoadingDomains] = useState(false);
  const [newDomain, setNewDomain] = useState("");
  const [addingDomain, setAddingDomain] = useState(false);
  const [domainError, setDomainError] = useState<string | null>(null);
  const [addDomainModalOpen, setAddDomainModalOpen] = useState(false);
  const [verifyingDomain, setVerifyingDomain] = useState<string | null>(null);
  const [, setDeletingDomain] = useState<string | null>(null);
  const [deleteDomainModalOpen, setDeleteDomainModalOpen] = useState(false);
  const [domainToDelete, setDomainToDelete] = useState<Domain | null>(null);
  const [dnsPropagationModalOpen, setDnsPropagationModalOpen] = useState(false);
  const [dnsModalContext, setDnsModalContext] = useState<"add" | "view">("add");
  const [expandedDnsInstructions, setExpandedDnsInstructions] = useState<
    Record<string, boolean>
  >({});
  const [dnsInstructions, setDnsInstructions] = useState<Record<string, any>>(
    {},
  );
  const [loadingDnsInstructions, setLoadingDnsInstructions] = useState<
    Record<string, boolean>
  >({});
  const [verificationMessage, setVerificationMessage] = useState<
    Record<string, { type: "success" | "error"; message: string }>
  >({});
  const [autoEnrollmentEnabled, setAutoEnrollmentEnabled] = useState(
    organizationDetails?.autoEnrollmentEnabled ?? false,
  );
  const [updatingAutoEnrollment, setUpdatingAutoEnrollment] = useState(false);

  const fetchDomains = async () => {
    if (!user.organization_id) return;
    setLoadingDomains(true);
    try {
      const domainsData = await listDomainsAPI(user.organization_id);
      setDomains(domainsData.domains || []);
    } catch (err: any) {
      console.error("Failed to fetch domains:", err);
    } finally {
      setLoadingDomains(false);
    }
  };

  useEffect(() => {
    fetchDomains();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.organization_id]);

  // Sync auto-enrollment state once org details load
  useEffect(() => {
    if (organizationDetails) {
      setAutoEnrollmentEnabled(organizationDetails.autoEnrollmentEnabled);
    }
  }, [organizationDetails]);

  const handleAddDomain = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user.organization_id) return;
    setAddingDomain(true);
    setDomainError(null);
    try {
      const normalizedDomain = normalizeDomain(newDomain);
      await addDomainAPI(user.organization_id, { domain: normalizedDomain });
      await fetchDomains();
      setNewDomain("");
      setAddDomainModalOpen(false);
      setDnsModalContext("add");
      setDnsPropagationModalOpen(true);
    } catch (err: any) {
      setDomainError(err.message || "Failed to add domain");
    } finally {
      setAddingDomain(false);
    }
  };

  const handleToggleDnsInstructions = async (
    domainId: string,
    isExpanding: boolean,
  ) => {
    if (!user.organization_id) return;
    setExpandedDnsInstructions((prev) => ({ ...prev, [domainId]: isExpanding }));
    if (isExpanding && !dnsInstructions[domainId]) {
      setLoadingDnsInstructions((prev) => ({ ...prev, [domainId]: true }));
      try {
        const instructions = await getVerificationInstructionsAPI(
          user.organization_id,
          domainId,
        );
        setDnsInstructions((prev) => ({
          ...prev,
          [domainId]: instructions.instructions.txt_record,
        }));
      } catch (err: any) {
        console.error("Failed to fetch DNS instructions:", err);
      } finally {
        setLoadingDnsInstructions((prev) => ({ ...prev, [domainId]: false }));
      }
    }
  };

  const handleVerifyDomain = async (
    domainId: string,
    force: boolean = false,
  ) => {
    if (!user.organization_id) return;
    setVerifyingDomain(domainId);
    setVerificationMessage((prev) => {
      const updated = { ...prev };
      delete updated[domainId];
      return updated;
    });
    try {
      const result = await verifyDomainAPI(
        user.organization_id,
        domainId,
        force,
      );
      if (result.status === "verification_failed") {
        if (
          result.message &&
          (result.message.includes("DNS propagation") ||
            result.message.includes("TXT record"))
        ) {
          setDnsModalContext("view");
          setDnsPropagationModalOpen(true);
        } else {
          setVerificationMessage((prev) => ({
            ...prev,
            [domainId]: {
              type: "error",
              message:
                result.message || result.error || "Domain verification failed.",
            },
          }));
        }
      } else if (
        result.status === "already_verified" ||
        result.status === "verified"
      ) {
        await fetchDomains();
        const successMessage =
          result.status === "already_verified"
            ? "Domain is already verified."
            : result.message || "Domain verified successfully!";
        setVerificationMessage((prev) => ({
          ...prev,
          [domainId]: { type: "success", message: successMessage },
        }));
        setDnsInstructions((prev) => {
          const updated = { ...prev };
          delete updated[domainId];
          return updated;
        });
        setExpandedDnsInstructions((prev) => {
          const updated = { ...prev };
          delete updated[domainId];
          return updated;
        });
        setTimeout(() => {
          setVerificationMessage((prev) => {
            const updated = { ...prev };
            delete updated[domainId];
            return updated;
          });
        }, 5000);
      }
    } catch (err: any) {
      const errorMessage =
        err.message ||
        "Domain verification failed. Please check your DNS records.";
      if (
        errorMessage.includes("DNS propagation") ||
        errorMessage.includes("TXT record")
      ) {
        setDnsModalContext("view");
        setDnsPropagationModalOpen(true);
      } else {
        setVerificationMessage((prev) => ({
          ...prev,
          [domainId]: { type: "error", message: errorMessage },
        }));
        setTimeout(() => {
          setVerificationMessage((prev) => {
            const updated = { ...prev };
            delete updated[domainId];
            return updated;
          });
        }, 10000);
      }
    } finally {
      setVerifyingDomain(null);
    }
  };

  const handleOpenDeleteDomainModal = (domain: Domain) => {
    setDomainToDelete(domain);
    setDeleteDomainModalOpen(true);
  };

  const handleCloseDeleteDomainModal = () => {
    setDeleteDomainModalOpen(false);
    setDomainToDelete(null);
  };

  const handleDeleteDomain = async () => {
    if (!domainToDelete || !user.organization_id) return;
    setDeletingDomain(domainToDelete.id);
    try {
      await deleteDomainAPI(user.organization_id, domainToDelete.id);
      await fetchDomains();
      handleCloseDeleteDomainModal();
    } catch (err: any) {
      console.error("Failed to delete domain:", err);
    } finally {
      setDeletingDomain(null);
    }
  };

  const handleToggleAutoEnrollment = async (enabled: boolean) => {
    if (!user.organization_id) return;
    setUpdatingAutoEnrollment(true);
    try {
      const result = await updateAutoEnrollmentAPI(user.organization_id, {
        auto_enrollment_enabled: enabled,
      });
      setAutoEnrollmentEnabled(enabled);
      alert(
        result.message ||
          `Auto-enrollment ${enabled ? "enabled" : "disabled"} successfully!`,
      );
    } catch (err: any) {
      const errorMessage =
        err.response?.detail ||
        err.message ||
        "Failed to update auto-enrollment setting";
      alert(`Failed to update auto enrollment: ${errorMessage}`);
    } finally {
      setUpdatingAutoEnrollment(false);
    }
  };

  const handleCopyDnsValue = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
    } catch (err) {
      console.error("Failed to copy to clipboard:", err);
    }
  };

  return (
    <Page>
      <PageHeader>
        <Box>
          <Typography level="h1">Domains</Typography>
          <Typography level="body-sm" color="neutral">
            Verify email domains to enable auto-enrollment
          </Typography>
        </Box>
        <Button
          variant="solid"
          color="primary"
          size="sm"
          onClick={() => setAddDomainModalOpen(true)}
          sx={{
            width: { xs: "100%", sm: "auto" },
            justifyContent: { xs: "center", sm: "flex-start" },
          }}
        >
          Add Domain
        </Button>
      </PageHeader>

      <Card>
        <CardContent>
          <Stack spacing={3}>
            {/* Domains List */}
            <Box>
              <Typography level="title-lg" sx={{ fontWeight: 600 }}>
                Domains
              </Typography>
              <Typography level="body-sm" color="neutral" sx={{ mt: 0.5 }}>
                Optional: Verify email domains to enable auto-enrollment.
                Without verified domains, only manual invitations work.
              </Typography>

              {loadingDomains ? (
                <Box sx={{ py: 3, textAlign: "center" }}>
                  <Loading />
                </Box>
              ) : domains.length === 0 ? (
                <Alert variant="soft" color="neutral">
                  <Typography level="body-sm">No domains added yet.</Typography>
                </Alert>
              ) : (
                <Stack spacing={2} sx={{ mt: 2 }}>
                  {domains.map((domain) => (
                    <Card key={domain.id} variant="outlined">
                      <CardContent>
                        <Stack spacing={2}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Box>
                              <Typography
                                level="title-md"
                                sx={{ fontWeight: 600 }}
                              >
                                {domain.domain}
                              </Typography>
                              <Stack
                                direction="row"
                                spacing={1}
                                sx={{ mt: 0.5 }}
                              >
                                <Chip
                                  size="sm"
                                  variant="soft"
                                  color={
                                    domain.verified ? "success" : "warning"
                                  }
                                >
                                  {domain.verified
                                    ? "Verified"
                                    : "Pending Verification"}
                                </Chip>
                                {domain.verified_at && (
                                  <Typography level="body-xs" color="neutral">
                                    Verified{" "}
                                    {new Date(
                                      domain.verified_at,
                                    ).toLocaleDateString()}
                                  </Typography>
                                )}
                              </Stack>
                            </Box>
                            <Stack direction="row" spacing={1}>
                              {!domain.verified && (
                                <>
                                  <Button
                                    variant="solid"
                                    color="primary"
                                    size="sm"
                                    onClick={() =>
                                      handleVerifyDomain(domain.id)
                                    }
                                    disabled={verifyingDomain === domain.id}
                                    loading={verifyingDomain === domain.id}
                                  >
                                    Verify
                                  </Button>
                                  {isDevFeaturesEnabled() && (
                                    <Button
                                      variant="outlined"
                                      color="warning"
                                      size="sm"
                                      onClick={() =>
                                        handleVerifyDomain(domain.id, true)
                                      }
                                      disabled={verifyingDomain === domain.id}
                                      startDecorator={<BugReportIcon />}
                                      title="Dev mode: Force verify (bypasses DNS check)"
                                    >
                                      Debug Verify
                                    </Button>
                                  )}
                                </>
                              )}
                              <IconButton
                                variant="outlined"
                                color="danger"
                                size="sm"
                                onClick={() =>
                                  handleOpenDeleteDomainModal(domain)
                                }
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Stack>
                          </Box>

                          {/* DNS Instructions (collapsible, unverified only) */}
                          {!domain.verified && (
                            <Box>
                              <Button
                                variant="plain"
                                size="sm"
                                onClick={() =>
                                  handleToggleDnsInstructions(
                                    domain.id,
                                    !expandedDnsInstructions[domain.id],
                                  )
                                }
                                endDecorator={
                                  expandedDnsInstructions[domain.id] ? (
                                    <ExpandLessIcon />
                                  ) : (
                                    <ExpandMoreIcon />
                                  )
                                }
                                sx={{ mb: 1 }}
                                loading={loadingDnsInstructions[domain.id]}
                              >
                                DNS Instructions
                              </Button>
                              {expandedDnsInstructions[domain.id] &&
                                dnsInstructions[domain.id] && (
                                  <Stack spacing={1}>
                                    <Typography
                                      level="body-sm"
                                      sx={{ fontWeight: 600 }}
                                    >
                                      Add this TXT record to your DNS:
                                    </Typography>
                                    <Box
                                      sx={{
                                        bgcolor: "background.level1",
                                        p: 1.5,
                                        borderRadius: "sm",
                                        fontFamily: "monospace",
                                        border: "1px solid",
                                        borderColor:
                                          "var(--joy-palette-divider)",
                                      }}
                                    >
                                      <Stack spacing={0.5}>
                                        {[
                                          {
                                            label: "Type:",
                                            value:
                                              dnsInstructions[domain.id].type,
                                          },
                                          {
                                            label: "Host:",
                                            value:
                                              dnsInstructions[domain.id].host,
                                          },
                                          {
                                            label: "TTL:",
                                            value:
                                              dnsInstructions[domain.id].ttl,
                                          },
                                        ].map(({ label, value }) => (
                                          <Box
                                            key={label}
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 1,
                                            }}
                                          >
                                            <Typography
                                              level="body-xs"
                                              sx={{
                                                minWidth: "80px",
                                                fontWeight: 600,
                                              }}
                                            >
                                              {label}
                                            </Typography>
                                            <Typography level="body-xs">
                                              {value}
                                            </Typography>
                                          </Box>
                                        ))}
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                          }}
                                        >
                                          <Typography
                                            level="body-xs"
                                            sx={{
                                              minWidth: "80px",
                                              fontWeight: 600,
                                            }}
                                          >
                                            Value:
                                          </Typography>
                                          <Box
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 0.5,
                                              flex: 1,
                                            }}
                                          >
                                            <Typography
                                              level="body-xs"
                                              sx={{
                                                flex: 1,
                                                wordBreak: "break-all",
                                              }}
                                            >
                                              {dnsInstructions[domain.id].value}
                                            </Typography>
                                            <IconButton
                                              size="sm"
                                              variant="plain"
                                              onClick={() =>
                                                handleCopyDnsValue(
                                                  dnsInstructions[domain.id]
                                                    .value,
                                                )
                                              }
                                            >
                                              <ContentCopyIcon fontSize="small" />
                                            </IconButton>
                                          </Box>
                                        </Box>
                                      </Stack>
                                    </Box>
                                    <Typography
                                      level="body-xs"
                                      color="neutral"
                                    >
                                      After adding the DNS record, click
                                      "Verify" above to check verification.
                                    </Typography>
                                  </Stack>
                                )}
                            </Box>
                          )}

                          {verificationMessage[domain.id]?.type ===
                            "success" && (
                            <Alert variant="soft" color="success">
                              {verificationMessage[domain.id].message}
                            </Alert>
                          )}
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              )}
            </Box>

            <Divider />

            {/* Auto-Enrollment */}
            <Box>
              <Typography level="title-lg" sx={{ mb: 2, fontWeight: 600 }}>
                Auto-Enrollment
              </Typography>
              <Stack spacing={1}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Switch
                    checked={autoEnrollmentEnabled}
                    onChange={(e) =>
                      handleToggleAutoEnrollment(e.target.checked)
                    }
                    disabled={
                      updatingAutoEnrollment ||
                      domains.filter((d) => d.verified).length === 0
                    }
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography level="body-md">
                      Enable auto-enrollment
                    </Typography>
                    <Typography level="body-sm" color="neutral">
                      Users with emails from verified domains will automatically
                      join your organization as members.
                    </Typography>
                  </Box>
                </Box>
                {domains.filter((d) => d.verified).length === 0 && (
                  <Alert variant="soft" color="neutral">
                    <Typography level="body-sm">
                      Verify at least one domain to enable auto-enrollment.
                    </Typography>
                  </Alert>
                )}
              </Stack>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Add Domain Modal */}
      <Modal
        open={addDomainModalOpen}
        onClose={() => {
          setAddDomainModalOpen(false);
          setNewDomain("");
          setDomainError(null);
        }}
      >
        <ModalDialog
          size="md"
          variant="outlined"
          sx={{ maxWidth: 500, width: "100%" }}
        >
          <DialogTitle>Add Domain</DialogTitle>
          <form onSubmit={handleAddDomain}>
            <Stack spacing={3}>
              {domainError && (
                <Alert color="danger" variant="soft">
                  {domainError}
                </Alert>
              )}
              <FormControl required>
                <FormLabel>Domain</FormLabel>
                <Input
                  type="text"
                  placeholder="acme.com"
                  value={newDomain}
                  onChange={(e) => setNewDomain(e.target.value)}
                  disabled={addingDomain}
                  size="md"
                  autoFocus
                />
                <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>
                  Enter the domain name (e.g., acme.com). Protocol and www will
                  be automatically removed.
                </Typography>
              </FormControl>
              <Stack
                direction="row"
                spacing={2}
                sx={{ justifyContent: "flex-end", mt: 2 }}
              >
                <Button
                  variant="outlined"
                  color="neutral"
                  onClick={() => {
                    setAddDomainModalOpen(false);
                    setNewDomain("");
                    setDomainError(null);
                  }}
                  disabled={addingDomain}
                  size="md"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="solid"
                  color="primary"
                  disabled={addingDomain || !newDomain.trim()}
                  loading={addingDomain}
                  size="md"
                >
                  Add Domain
                </Button>
              </Stack>
            </Stack>
          </form>
        </ModalDialog>
      </Modal>

      {/* Delete Domain Modal */}
      <Modal open={deleteDomainModalOpen} onClose={handleCloseDeleteDomainModal}>
        <ModalDialog
          size="md"
          variant="outlined"
          sx={{ maxWidth: 400, width: "100%" }}
        >
          <DialogTitle>Delete Domain</DialogTitle>
          <Stack spacing={3}>
            <Typography level="body-md">
              Are you sure you want to remove{" "}
              <strong>{domainToDelete?.domain}</strong>? This will disable
              auto-enrollment for this domain.
            </Typography>
            <Stack
              direction="row"
              spacing={2}
              sx={{ justifyContent: "flex-end", mt: 2 }}
            >
              <Button
                variant="outlined"
                color="neutral"
                onClick={handleCloseDeleteDomainModal}
                size="md"
              >
                Cancel
              </Button>
              <Button
                variant="solid"
                color="danger"
                onClick={handleDeleteDomain}
                size="md"
              >
                Delete Domain
              </Button>
            </Stack>
          </Stack>
        </ModalDialog>
      </Modal>

      {/* DNS Propagation Modal */}
      <Modal
        open={dnsPropagationModalOpen}
        onClose={() => setDnsPropagationModalOpen(false)}
      >
        <ModalDialog
          size="md"
          variant="outlined"
          sx={{ maxWidth: 500, width: "100%" }}
        >
          <DialogTitle>
            {dnsModalContext === "add"
              ? "Domain Added Successfully"
              : "DNS Verification Instructions"}
          </DialogTitle>
          <Stack spacing={3}>
            <Alert variant="soft" color="warning">
              <Typography level="body-md">
                Please add the TXT record and wait a few minutes for DNS
                propagation.
              </Typography>
            </Alert>
            <Typography level="body-sm" color="neutral">
              After adding the DNS record to your domain provider, click
              "Verify" on the domain card to check verification. DNS changes can
              take a few minutes to propagate.
            </Typography>
            <Stack
              direction="row"
              spacing={2}
              sx={{ justifyContent: "flex-end", mt: 2 }}
            >
              <Button
                variant="solid"
                color="primary"
                onClick={() => setDnsPropagationModalOpen(false)}
                size="md"
              >
                Got it
              </Button>
            </Stack>
          </Stack>
        </ModalDialog>
      </Modal>
    </Page>
  );
};

export default OrgSettingsDomains;
